import React, { Fragment, useRef, useEffect, useState } from 'react';
import { Plus, ChevronDown, Search, X, Pencil, AlertCircle, ScanSearch, Sparkles } from 'lucide-react';
import { LayoutGrid, List } from 'lucide-react'; // Keep these for the view toggle
import { Menu, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { PlaybookEventConfig, PlaybookTransformType } from '@/types/playbook';
import { EditorSelection } from './PlaybookEditorTab';
import { UnifiedSchema } from '@/types/schema';
import { PLAYBOOK_TRANSFORM_CONFIGS } from '@/config/playbookTransforms';

interface PlaybookTransformEditorPanelHeaderProps {
  selection: EditorSelection;
  selectedEvent: PlaybookEventConfig | null;
  getSectionTitle: () => string;
  isEventNameEditing: boolean;
  setIsEventNameEditing: (isEditing: boolean) => void;
  isEventCodeNameEditing: boolean;
  setIsEventCodeNameEditing: (isEditing: boolean) => void;
  isEventDescriptionEditing: boolean;
  setIsEventDescriptionEditing: (isEditing: boolean) => void;
  eventCodeNameError: string | null;
  eventNameInputRef: React.RefObject<HTMLInputElement>;
  eventCodeNameInputRef: React.RefObject<HTMLInputElement>;
  eventDescriptionInputRef: React.RefObject<HTMLTextAreaElement>;
  handleEventChange: (eventId: string, field: 'name' | 'codeName' | 'description', value: string) => void;
  allowUndefinedTransforms: boolean;
  // New props for the functionality
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  viewMode: 'card' | 'table';
  setViewMode: (mode: 'card' | 'table') => void;
  onAutoMap: () => void;
  onAIMatch: () => void;
  onAddTransform: (type: PlaybookTransformType, selection: EditorSelection) => void;
  inputSchema: UnifiedSchema | null;
  outputSchema: UnifiedSchema | null;
  totalTransformsCount: number;
}

const PlaybookTransformEditorPanelHeader: React.FC<PlaybookTransformEditorPanelHeaderProps> = ({
  selection,
  selectedEvent,
  getSectionTitle,
  isEventNameEditing,
  setIsEventNameEditing,
  isEventCodeNameEditing,
  setIsEventCodeNameEditing,
  isEventDescriptionEditing,
  setIsEventDescriptionEditing,
  eventCodeNameError,
  eventNameInputRef,
  eventCodeNameInputRef,
  eventDescriptionInputRef,
  handleEventChange,
  allowUndefinedTransforms,
  searchTerm,
  setSearchTerm,
  viewMode,
  setViewMode,
  onAutoMap,
  onAIMatch,
  onAddTransform,
  inputSchema,
  outputSchema,
  totalTransformsCount,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const scrollableContainer = header.closest('.overflow-y-auto');
    if (!scrollableContainer) return; // No scrollable container found, cannot make sticky

    const handleScroll = () => {
      // Since we removed the p-5 padding, the header starts at the very top (offsetTop = 0)
      // We want it to become sticky when we start scrolling, so we check if scrollTop > 0
      if (scrollableContainer.scrollTop > 0) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    scrollableContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollableContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Don't render the header at all for connection type
  if (selection.type === 'connection') {
    return null;
  }

  return (
    <div
      ref={headerRef}
      className={clsx(
        "flex-shrink-0",
        isSticky 
          ? "sticky top-0 z-50 bg-gray-50 dark:bg-gray-900 border-b dark:border-gray-900 shadow-sm -mx-4 px-8 py-4" 
          : "px-6 py-4"
      )}
    >
      <div className={clsx(
        "flex items-center justify-between transition-all duration-300 ease-in-out",
        isSticky ? "mb-4" : "mb-4"
      )}>
        <div className="flex-1">
          {selectedEvent ? (
            <div className={clsx(
              "transition-all duration-300 ease-in-out",
              isSticky && "transform scale-90 origin-left"
            )}>
              {/* Event Name - always visible but smaller when sticky */}
              <div className="flex items-center justify-between">
                {isEventNameEditing ? (
                  <input
                    ref={eventNameInputRef}
                    type="text"
                    value={selectedEvent.name}
                    onChange={(e) => {
                      handleEventChange(selectedEvent.id, 'name', e.target.value);
                    }}
                    onBlur={() => setIsEventNameEditing(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setIsEventNameEditing(false);
                        e.currentTarget.value = selectedEvent.name;
                      } else if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                    className={clsx(
                      "text-gray-900 dark:text-white font-bold bg-transparent border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 flex-grow transition-all duration-300",
                      isSticky ? "text-lg" : "text-2xl"
                    )}
                    placeholder="Event Friendly Name"
                    autoFocus
                    tabIndex={1}
                  />
                ) : (
                  <h2
                    className={clsx(
                      "flex items-center text-gray-900 dark:text-white font-bold px-2 py-1 -mx-2 -my-1 group cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-all duration-300",
                      isSticky ? "text-lg" : "text-2xl"
                    )}
                    onClick={() => setIsEventNameEditing(true)}
                  >
                    {selectedEvent.name || 'Unnamed Event'}
                    <Pencil size={16} className="ml-2 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h2>
                )}
                <div className={clsx(
                  "flex items-center gap-2 ml-4 transition-all duration-300 ease-in-out overflow-hidden",
                  isSticky ? "max-w-0 opacity-0" : "max-w-xs opacity-100"
                )}>
                  {isEventCodeNameEditing ? (
                    <div className="relative">
                      <input
                        ref={eventCodeNameInputRef}
                        type="text"
                        value={selectedEvent.codeName || ''}
                        onChange={(e) => handleEventChange(selectedEvent.id, 'codeName', e.target.value)}
                        onBlur={() => setIsEventCodeNameEditing(false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setIsEventCodeNameEditing(false);
                            e.currentTarget.value = selectedEvent.codeName || '';
                          } else if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        className={clsx(
                          `px-2 py-1 rounded-md text-sm font-mono bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border`,
                          eventCodeNameError ? 'border-red-500' : 'border-transparent'
                        )}
                        placeholder="event_code_name"
                        autoFocus
                        tabIndex={2}
                      />
                      {eventCodeNameError && (
                        <div className="absolute top-full left-0 mt-1 text-xs text-red-500 flex items-center">
                          <AlertCircle size={12} className="inline mr-1" />
                          {eventCodeNameError}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span
                      className={clsx(
                        "inline-flex items-center px-2 py-1 rounded-md text-sm font-mono font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 transition-colors group whitespace-nowrap",
                        allowUndefinedTransforms ? "cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800" : "opacity-50 cursor-not-allowed"
                      )}
                      onClick={() => allowUndefinedTransforms && setIsEventCodeNameEditing(true)}
                    >
                      {selectedEvent.codeName || 'set_code_name'}
                      {allowUndefinedTransforms && (
                        <span className="flex items-center w-0 overflow-hidden group-hover:w-6 transition-all duration-300">
                          <Pencil size={14} className="ml-1 text-blue-600 group-hover:text-blue-800 dark:group-hover:text-blue-200" />
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Event Description - hidden when sticky */}
              <div className={clsx(
                "transition-all duration-300 ease-in-out overflow-hidden",
                isSticky ? "max-h-0 opacity-0 mt-0" : "max-h-20 opacity-100 mt-3"
              )}>
                {isEventDescriptionEditing ? (
                  <textarea
                    ref={eventDescriptionInputRef}
                    value={selectedEvent.description || ''}
                    onChange={(e) => handleEventChange(selectedEvent.id, 'description', e.target.value)}
                    onBlur={() => setIsEventDescriptionEditing(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setIsEventDescriptionEditing(false);
                        e.currentTarget.value = selectedEvent.description || '';
                      } else if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                    className="text-gray-700 dark:text-gray-300 text-sm bg-transparent border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none w-full"
                    placeholder="Event Description"
                    rows={2}
                    autoFocus
                    tabIndex={3}
                  />
                ) : (
                  <p
                    className="flex items-center text-gray-700 dark:text-gray-300 text-sm px-2 py-1 -mx-2 -my-1 group cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md"
                    onClick={() => setIsEventDescriptionEditing(true)}
                  >
                    {selectedEvent.description || 'Add a description for this event...'}
                    <span className="flex items-center w-0 overflow-hidden group-hover:w-6 transition-all duration-300">
                      <Pencil size={14} className="ml-1 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                    </span>
                  </p>
                )}
              </div>
            </div>
          ) : (
            <h3 className={clsx(
              "font-semibold text-gray-900 dark:text-white transition-all duration-300",
              isSticky ? "text-lg" : "text-lg"
            )}>
              {getSectionTitle()}
            </h3>
          )}
        </div>
      </div>

      {/* Controls Row - View Toggle, Search, Auto-map, AI matching, Add Transform */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* View Mode Toggle */}
        <div className="inline-flex rounded-md shadow-sm" role="group">
          <button
            type="button"
            className={clsx(
              "px-3 py-1 h-8 text-sm font-medium border-y border-l rounded-l-md",
              viewMode === 'card'
                ? "bg-blue-600 text-white border-blue-700"
                : "bg-white text-gray-700 border-gray-300 hover:bg-blue-100 dark:text-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-blue-400/20"
            )}
            onClick={() => setViewMode('card')}
            title="Card View"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={clsx(
              "px-3 py-1 h-8 text-sm font-medium border-y border-r rounded-r-md",
              viewMode === 'table'
                ? "bg-blue-600 text-white border-blue-700"
                : "bg-white text-gray-700 border-gray-300 hover:bg-blue-100 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-blue-400/20"
            )}
            onClick={() => setViewMode('table')}
            title="Table View"
          >
            <List className="h-4 w-4" />
          </button>
        </div>

        {/* Search Input */}
        <div className="flex-1 relative flex items-center gap-2 min-w-[200px]">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder={`Filter ${totalTransformsCount} transforms by name, input, output...`}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm dark:placeholder-gray-400"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            tabIndex={4}
          />
          {searchTerm && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              onClick={() => setSearchTerm('')}
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Auto-map Button */}
          <button
            onClick={onAutoMap}
            disabled={!inputSchema || !outputSchema}
            className="flex justify-center items-center gap-0.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 shadow hover:shadow-md text-gray-700 dark:text-gray-200 cursor-pointer"
            title="Auto-map matching properties"
          >
            <ScanSearch className="h-4 w-4 mr-2" />
            Auto-map
          </button>

          {/* AI Matching Button */}
          <button
            onClick={onAIMatch}
            disabled={!inputSchema || !outputSchema}
            className="flex justify-center items-center gap-0.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 shadow hover:shadow-md text-gray-700 dark:text-gray-200 cursor-pointer"
            title="AI matching for unmapped properties"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            AI matching
          </button>

          {/* Add Transform Menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="px-3 py-1.5 text-sm bg-blue-600 font-medium text-white rounded hover:bg-blue-700 transition-colors flex items-center shadow-sm whitespace-nowrap">
              <Plus className="h-4 w-4 mr-2" />
              Add Transform
              <ChevronDown className="h-3 w-3 ml-1" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-50 mt-1 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  {PLAYBOOK_TRANSFORM_CONFIGS.map((config) => (
                    <Menu.Item key={config.type}>
                      {({ active }) => {
                        const Icon = config.icon;
                        return (
                          <button
                            onClick={() => onAddTransform(config.type, selection)}
                            className={`${
                              active ? 'bg-gray-100 dark:bg-gray-700' : ''
                            } group flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                          >
                            <Icon size={16} className={clsx("mr-2", config.colorClass.replace('bg-', 'text-'))} />
                            {
                              config.type === 'mapping' ? <><span className="underline">M</span>apping</> :
                              config.type === 'enrichment' ? <><span className="underline">E</span>nrichment</> :
                              config.type === 'expression' ? <>E<span className="underline">x</span>pression</> :
                              config.type === 'spreading' ? <><span className="underline">S</span>preading</> :
                              config.type === 'filter' ? <><span className="underline">F</span>ilter</> :
                              config.label
                            }
                          </button>
                        );
                      }}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
};

export default PlaybookTransformEditorPanelHeader;
